"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { useConvex, useMutation } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { projectDocumentErrorMessage } from "@/lib/project-documents/errors";
import { validateDocumentFile } from "@/lib/project-documents/upload-file";
type UploadAttempt = {
  file: File;
  replacesDocumentId?: Id<"project_documents">;
  documentId?: Id<"project_documents">;
  cancelled: boolean;
};
type UploadState = {
  name: string;
  progress: number;
  phase: "uploading" | "validating" | "failed";
  error?: string;
  /** False when the server rejected the file itself, so sending it again cannot succeed. */
  retryable?: boolean;
  replacesDocumentId?: Id<"project_documents">;
};
function sendFile(url: string, file: File, mimeType: string, onProgress: (value: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", url);
    request.setRequestHeader("Content-Type", mimeType);
    request.responseType = "json";
    request.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(Math.round(event.loaded / event.total * 100));
    };
    request.onload = () => {
      const payload: unknown = request.response;
      if (request.status === 200) resolve();else reject(new Error(payload && typeof payload === "object" && "error" in payload && typeof payload.error === "string" ? payload.error : "Não foi possível enviar o arquivo. Tente novamente."));
    };
    request.onerror = () => reject(new Error("Conexão interrompida. Tente novamente."));
    request.send(file);
  });
}
export function useProjectDocumentUpload(projectId: Id<"projects">) {
  const convex = useConvex();
  const reserve = useMutation(api.projectDocuments.mutations.reserveProjectDocumentUpload);
  const cancelReservation = useMutation(api.projectDocuments.mutations.cancelProjectDocumentUpload);
  const attempt = useRef<UploadAttempt | null>(null);
  const running = useRef(false);
  const mounted = useRef(true);
  const [state, setState] = useState<UploadState | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (attempt.current) attempt.current.cancelled = true;
    };
  }, []);
  function update(value: UploadState | null) {
    if (mounted.current) setState(value);
  }
  async function discard(current: UploadAttempt) {
    if (current.documentId) {
      const status = await convex.query(api.projectDocuments.queries.getUploadStatus, {
        documentId: current.documentId
      });
      // A commit that won the race is already a document; cancellation must not undo it.
      if (status !== "ready" && status !== null) await cancelReservation({
        documentId: current.documentId
      });
    }
    attempt.current = null;
    update(null);
  }
  async function run(current_0: UploadAttempt) {
    if (running.current) return;
    running.current = true;
    update({
      name: current_0.file.name,
      progress: 0,
      phase: "uploading",
      replacesDocumentId: current_0.replacesDocumentId
    });
    try {
      const mimeType = validateDocumentFile(current_0.file);
      if (!current_0.documentId) {
        const reservation = await reserve({
          projectId,
          fileName: current_0.file.name,
          claimedBytes: current_0.file.size,
          mimeType,
          ...(current_0.replacesDocumentId ? {
            replacesDocumentId: current_0.replacesDocumentId
          } : {})
        });
        current_0.documentId = reservation.documentId;
      }
      if (current_0.cancelled) {
        await discard(current_0);
        return;
      }
      const uploadUrl = `/api/projects/${projectId}/documents/${current_0.documentId}/upload`;
      await sendFile(uploadUrl, current_0.file, mimeType, progress => update({
        name: current_0.file.name,
        progress,
        phase: progress === 100 ? "validating" : "uploading",
        replacesDocumentId: current_0.replacesDocumentId
      }));
      if (current_0.cancelled) {
        await discard(current_0).catch(() => undefined);
        return;
      }
      attempt.current = null;
      update(null);
      if (mounted.current) toast.success(current_0.replacesDocumentId ? "Documento substituído" : "Documento enviado");
    } catch (error) {
      const message = projectDocumentErrorMessage(error);
      let retryable = true;
      if (current_0.documentId) {
        const status_0 = await convex.query(api.projectDocuments.queries.getUploadStatus, {
          documentId: current_0.documentId
        }).catch(() => undefined);
        if (status_0 === "ready") {
          attempt.current = null;
          update(null);
          return;
        }
        if (status_0 === "rejected" || status_0 === null || status_0 === "deleted") {
          retryable = status_0 !== "rejected";
          if (status_0 === "rejected") await cancelReservation({
            documentId: current_0.documentId
          }).catch(() => undefined);
          current_0.documentId = undefined;
        }
      }
      if (current_0.cancelled) {
        await discard(current_0).catch(() => undefined);
        return;
      }
      // The server retains owned bytes, so a lost response can be retried safely.
      update({
        name: current_0.file.name,
        progress: 0,
        phase: "failed",
        error: message,
        retryable,
        replacesDocumentId: current_0.replacesDocumentId
      });
    } finally {
      running.current = false;
    }
  }
  async function start(file: File, replacesDocumentId?: Id<"project_documents">) {
    if (attempt.current || running.current) return;
    try {
      validateDocumentFile(file);
    } catch (error_0) {
      toast.error(projectDocumentErrorMessage(error_0));
      return;
    }
    const current_1 = {
      file,
      replacesDocumentId,
      cancelled: false
    };
    attempt.current = current_1;
    await run(current_1);
  }
  async function cancel() {
    const current_2 = attempt.current;
    if (!current_2) return;
    current_2.cancelled = true;
    try {
      // Revoke the reservation immediately, including while the server receives bytes.
      await discard(current_2);
    } catch (error_1) {
      toast.error(projectDocumentErrorMessage(error_1));
    }
  }
  return {
    state,
    start,
    cancel,
    retry: () => {
      const current_3 = attempt.current;
      if (current_3) {
        current_3.cancelled = false;
        return run(current_3);
      }
    }
  };
}